import { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

import SolidPauseIcon from './SolidPauseIcon';
import SolidPlayIcon from './SolidPlayIcon';

export function AudioPlayer({
  src,
  backgroundColor = 'bg-received',
  foregroundColor = 'text-primary-foreground',
  className = '',
}) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleProgressChange = (value) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = value[0];
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      audio.volume = 1; // Always restore to full volume
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';

    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className={cn(
        'flex h-9 max-w-[500px] items-center gap-3 px-2 py-1',
        backgroundColor,
        className
      )}
    >
      <audio ref={audioRef} src={src} preload='metadata' />

      {/* Play/Pause Button */}
      {isPlaying ? (
        <SolidPauseIcon
          onClick={togglePlay}
          className={cn('h-4 w-4 cursor-pointer', foregroundColor)}
        />
      ) : (
        <SolidPlayIcon
          onClick={togglePlay}
          className={cn('h-4 w-4 cursor-pointer', foregroundColor)}
        />
      )}

      {/* Progress Bar */}
      <div className='flex flex-1 items-center gap-2'>
        <span className={cn('min-w-[35px] text-xs', foregroundColor)}>
          {formatTime(currentTime)}
        </span>

        <Slider
          value={[currentTime]}
          max={duration || 100}
          step={1}
          onValueChange={handleProgressChange}
          className='flex-1'
        />

        <span className={cn('min-w-[35px] text-xs', foregroundColor)}>
          {formatTime(duration)}
        </span>
      </div>

      {/* Volume Controls */}
      <div className='flex items-center'>
        {isMuted ? (
          <VolumeX
            onClick={toggleMute}
            className={cn('h-4 w-4 cursor-pointer', foregroundColor)}
          />
        ) : (
          <Volume2
            onClick={toggleMute}
            className={cn('h-4 w-4 cursor-pointer', foregroundColor)}
          />
        )}
      </div>
    </div>
  );
}
